import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Button,
  Select,
  Row,
  Col,
  Input,
  Form,
  Modal,
  Divider,
  notification,
  message,
  Card,
} from "antd";
import {
  PlusOutlined,
  ExportOutlined,
  EditOutlined,
  DeleteOutlined,
  PrinterOutlined,
  StopOutlined,
  CheckCircleOutlined,
  CopyFilled,
} from "@ant-design/icons";
import { MesServices } from "../Services/MesServices";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

const { Option } = Select;

const Documents = () => {
  const [modal, contextHolder] = Modal.useModal();
  const [api, notificationHolder] = notification.useNotification();
  const [messageApi, messageHolder] = message.useMessage();

  const numcont = localStorage.getItem("numcont");
  const codesoc = localStorage.getItem("codesoc");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [filteredFournisseurs, setFilteredFournisseurs] = useState([]);
  const [filteredBenefData, setFilteredBenefData] = useState([]);
  const [filteredTypeDocData, setFilteredTypeDocData] = useState([]);
  const [filteredSpecialiteData, setFilteredSpecialiteData] = useState([]);
  const [exercices, setExercices] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [documents, setDocuments] = useState([]);
  const [benefData, setBenefData] = useState([]);
  const [typeDocData, setTypeDocData] = useState([]);
  const [prestationData, setPrestationData] = useState([]);
  const [fournisseurData, setFournisseurData] = useState([]);
  const [specialiteData, setSpecialiteData] = useState([]);
  const [loading, setLoading] = useState(false); // Pour afficher un loader pendant le chargement
  const [filteredData, setFilteredData] = useState([]); // Données filtrées
  const [filterEtat, setFilterEtat] = useState("Tous"); // Filtre par état
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [isTypeBenefSelected, setIsTypeBenefSelected] = useState(false);
  const [isTypeDocSelected, setIsTypeDocSelected] = useState(false);
  const [isCodeSpeSelected, setIsCodeSpeSelected] = useState(false);
  const [summaryData, setSummaryData] = useState([]);

  const mesServices = new MesServices();

  const filteredDocuments = useMemo(
    () =>
      documents.filter((doc) =>
        filterEtat === "Tous" ? true : doc.etat === filterEtat
      ),
    [documents, filterEtat]
  );

  const parseNumber = (value) => {
    if (typeof value === "number") return value;
    if (typeof value !== "string") return null;

    let sanitized = value
      .replace(/\u00A0/g, "")
      .replace(/\u202F/g, "")
      .replace(/\s/g, "");

    const lastComma = sanitized.lastIndexOf(",");
    const lastDot = sanitized.lastIndexOf(".");

    if (lastComma !== -1 && lastDot !== -1) {
      if (lastComma > lastDot) {
        sanitized = sanitized.replace(/\./g, "").replace(",", ".");
      } else {
        sanitized = sanitized.replace(/,/g, "");
      }
    } else if (lastComma !== -1) {
      sanitized = sanitized.replace(/,/g, ".");
    }

    const num = Number(sanitized);
    return Number.isFinite(num) ? num : null;
  };

  const totals = useMemo(() => {
    return summaryData.reduce(
      (acc, doc) => {
        const montdoc = parseNumber(doc.montdoc);
        const qpmut = parseNumber(doc.qpmut);
        const ticket = parseNumber(doc.ticket);
        const fexclus = parseNumber(doc.fexclus);

        acc.montdoc += montdoc ?? 0;
        acc.qpmut += qpmut ?? 0;
        acc.ticket += ticket ?? 0;
        acc.fexclus += fexclus ?? 0;
        return acc;
      },
      { montdoc: 0, qpmut: 0, ticket: 0, fexclus: 0 }
    );
  }, [summaryData]);

  const formatAmount = (value) =>
    Number.isFinite(value) ? value.toLocaleString("fr-FR") : "—";

  const formatCellAmount = (value) => {
    const parsed = parseNumber(value);
    if (parsed === null) {
      return value ?? "—";
    }
    return formatAmount(parsed);
  };

  useEffect(() => {
    setSummaryData(filteredDocuments);
  }, [filteredDocuments]);

  const handleTableChange = (_, __, ___, extra) => {
    if (extra?.action === "paginate") {
      return;
    }
    if (Array.isArray(extra?.currentDataSource)) {
      setSummaryData(extra.currentDataSource);
    } else {
      setSummaryData(filteredDocuments);
    }
  };

  // Charger les exercices au montage
  useEffect(() => {
    const fetchExercices = async () => {
      try {
        setLoading(true);

        const data = await mesServices.getDocExercice(numcont);
        setExercices(data);
        if (data.length > 0) {
          setSelectedYear(data[0]); // Sélectionner par défaut le premier exercice
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des exercices :", error);
      } finally {
        setLoading(false);
      }
    };

    fetchExercices();
  }, [numcont]);

  // Charger les documents en fonction de l'exercice sélectionné

  const fetchDocuments = async () => {
    if (!selectedYear) return;
    try {
      setLoading(true);

      const data = await mesServices.getDocsPart(numcont, selectedYear);
      setDocuments(data);
    } catch (error) {
      console.error("Erreur lors de la récupération des documents :", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchDocuments();
  }, [selectedYear, numcont]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [benefs, typesdocs, fournisseurs, prestations, specialites] =
          await Promise.all([
            mesServices.getBenef(numcont),
            mesServices.getTypeDoc(numcont),
            mesServices.getFournisseurs(),
            mesServices.getPrestation(),
            mesServices.getSpecialites(),
          ]);

        setBenefData(benefs);
        setTypeDocData(typesdocs);
        setFournisseurData(fournisseurs);
        setPrestationData(prestations);
        setSpecialiteData(specialites);
      } catch (error) {
        console.error("Erreur lors de la récupération des données :", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [numcont]);

  // Gestion de la sélection d'année
  const handleYearChange = (year) => {
    setSelectedYear(year);
  };




  // useEffect(() => {
  //   const currentTypedoc = form.getFieldValue('typedoc');
  //   // Filtrer les spécialités en fonction du typedoc
  //   const updatedSpecialites =
  //     currentTypedoc === 'LGI'
  //       ? specialiteData.filter(
  //         (specialite) => specialite.codespe !== '06' && specialite.codespe !== '04'
  //       )
  //       : specialiteData; // Si ce n'est pas LGI, afficher toutes les spécialités

  //   setFilteredSpecialiteData(updatedSpecialites);

  // }, []); // Réexécuter lorsque les données ou le formulaire changent


  // Gérer les changements de typedoc dans le formulaire
  const handleTypedocChange = (value) => {
    if (value === 'BPI') {
      setSpecialiteData(specialiteData); // Réinitialiser les spécialités si un autre type est sélectionné que BPI ou BCI
      form.setFieldsValue({ codeprest: '06' }); // Réinitialiser les champs "Spécialité", "Prestation" et "Fournisseur" lors du changement
      handleSpecialiteChange('06');
      setIsTypeDocSelected(true);

    } else if (value === 'BCI') {
      form.setFieldsValue({ codeprest: '04' }); // Réinitialiser les champs "Spécialité", "Prestation" et "Fournisseur" lors du changement
      handleSpecialiteChange('04');
      setIsTypeDocSelected(true);
    } else {
      form.setFieldsValue({ codeprest: null, codefourn: null, natprest: null }); // Réinitialiser les champs "Spécialité", "Prestation" et "Fournisseur" lors du changement
      setIsTypeDocSelected(false);
      setFilteredSpecialiteData(specialiteData.filter((specialite) => specialite.codespe !== '06' && specialite.codespe !== '04')); // Afficher toutes les spécialités pour les autres types
    }

    // form.setFieldsValue({ codeprest: null }); // Réinitialiser le champ "Spécialité" lors du changement
    // const updatedSpecialites =
    //   value === 'LGI'
    //     ? specialiteData.filter(
    //       (specialite) => specialite.codespe !== '06' && specialite.codespe !== '04'
    //     )
    //     : specialiteData;

    // setFilteredSpecialiteData(updatedSpecialites);
  };


  // Gestion du filtre par état
  useEffect(() => {
    if (filterEtat === "Tous") {
      setFilteredData(documents); // Affiche toutes les données
    } else {
      setFilteredData(documents.filter((doc) => doc.etat === filterEtat));
    }
  }, [filterEtat, documents]);
  const showModal = () => {
    setIsEditMode(false);
    setIsModalOpen(true);
  };



  const handleOk = async () => {
    try {
      const values = await form.validateFields(); // Récupérer les valeurs du formulaire
      setLoading(true);

      if (isEditMode) {
        // Construire le payload pour la mise à jour
        const payload = {
          ...editingRecord, // Inclure toutes les données originales de la ligne
          ...values, // Écraser les champs modifiés avec les valeurs du formulaire
        };

        // Appeler l'API de mise à jour
        const response = await mesServices.updateDoc(payload);
        api.success({
          message: "Succès",
          description: "Le document a été mis à jour avec succès.",
        });
      } else {
        const payload = {
          ...values,
          codesoc: codesoc, // Récupérer codesoc depuis le bénéficiaire
          user_id: numcont,
          matripart: numcont,
          datedoc: dayjs().format("YYYY-MM-DD"), // Date actuelle
          datepresc: dayjs().format("YYYY-MM-DD"), // Date actuelle
        };

        // Appeler l'API d'ajout
        const response = await mesServices.addDoc(payload);
        api.success({
          message: "Succès",
          description: "Le document a été ajouté avec succès.",
        });
      }

      fetchDocuments(); // Recharger les documents
      form.resetFields(); // Réinitialiser le formulaire
      setIsModalOpen(false); // Fermer le modal
    } catch (error) {
      console.error("Erreur :", error);
      api.error({
        message: "Erreur",
        description: "Une erreur s'est produite. Veuillez réessayer.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Réinitialiser tous les champs
    form.resetFields();

    // Réappliquer les valeurs initiales pour "Type de Document", "Prescripteur" et "Nature Prestation"
    const activeDocs = typeDocData.filter((type) => type.actif === true);
    form.setFieldsValue({
      typedoc: null, // Type de Document
      natprest: null, // Nature Prestation
      codepresc: null, // Prescripteur
    });

    // Fermer le modal
    setIsModalOpen(false);
  };

  // Colonnes du tableau
  const columns = [
    {
      title: "Numéro",
      dataIndex: "numdoc",
      key: "numdoc",
      sorter: (a, b) => a.numdoc - b.numdoc,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Rechercher"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Row justify="space-between">
            <Button
              type="primary"
              onClick={confirm}
              size="small"
              style={{ width: 90, marginRight: 8 }}
            >
              Rechercher
            </Button>
            <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
              Réinitialiser
            </Button>
          </Row>
        </div>
      ),
      onFilter: (value, record) =>
        record.numdoc.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: "Type",
      dataIndex: "typedoc",
      key: "typedoc",
      sorter: (a, b) => a.typedoc - b.typedoc,
      filters: [
        ...new Set(
          documents.map((item) => item?.typedoc).filter((value) => value) // Éliminer les valeurs `null` ou `undefined`
        ),
      ].map((value) => ({
        text: value,
        value: value,
      })),
      onFilter: (value, record) => record.typedoc === value,

      render: (typedoc) => {
        switch (typedoc) {
          case "LGI":
            return "Lettre de Garantie";
          case "BPI":
            return "Bon Pharmacie";
          case "BCI":
            return "Bon Optique";
          case "LHI":
            return "Lettre d'Hospitalisation";
          default:
            return "Inconnu"; // Si aucun type ne correspond
        }
      },
    },

    {
      title: "Date",
      dataIndex: "datedoc",
      key: "datedoc",
      render: (text) => (text ? dayjs(text).format("DD/MM/YYYY") : "—"),
      sorter: (a, b) => a.datedoc - b.datedoc,
    },
    {
      title: "Type Bénéf.",
      dataIndex: "typebenef",
      key: "typebenef",
      sorter: (a, b) => a.typebenef.localeCompare(b.typebenef),
      filters: [
        ...new Set(
          documents
            .map((item) => item?.typebenef) // Récupérer toutes les valeurs `typebenef`
            .filter((value) => value) // Éliminer les valeurs `null` ou `undefined`
        ),
      ].map((value) => ({
        text: (() => {
          switch (value) {
            case "1":
              return "Participant";
            case "2":
              return "Epouse";
            case "3":
              return "Enfant";
            case "4":
              return "Epoux";
            case "5":
              return "Ascendant";
            case "6":
              return "Autre";
            default:
              return "Inconnu";
          }
        })(),
        value: value, // Utiliser la valeur brute pour le filtre
      })),
      onFilter: (value, record) => record.typebenef === value,
      render: (typebenef) => {
        switch (typebenef) {
          case "1":
            return "Participant";
          case "2":
            return "Epouse";
          case "3":
            return "Enfant";
          case "4":
            return "Epoux";
          case "5":
            return "Ascendant";
          case "6":
            return "Autre";
          default:
            return "Inconnu"; // Si aucun type ne correspond
        }
      },
    },
    {
      title: "Bénéficiaire",
      dataIndex: "beneficiaire",
      key: "beneficiaire",
      sorter: (a, b) => a.beneficiaire - b.beneficiaire,
      filters: [
        ...new Set(
          documents.map((item) => item?.beneficiaire).filter((value) => value) // Éliminer les valeurs `null` ou `undefined`
        ),
      ].map((value) => ({
        text: value,
        value: value,
      })),
      onFilter: (value, record) => record.beneficiaire === value,
    },
    {
      title: "Prestataire",
      dataIndex: "fournisseur",
      key: "fournisseur",
      sorter: (a, b) => a.fournisseur - b.fournisseur,
      filters: [
        ...new Set(
          documents.map((item) => item?.fournisseur).filter((value) => value) // Éliminer les valeurs `null` ou `undefined`
        ),
      ].map((value) => ({
        text: value,
        value: value,
      })),
      onFilter: (value, record) => record.fournisseur === value,
    },
    {
      title: "Etat",
      dataIndex: "etat",
      key: "etat",
      align: "center",
      render: (etat) =>
        etat === "OK" ? (
          <span style={{ color: "green" }}>
            <CheckCircleOutlined />
          </span>
        ) : (
          <span style={{ color: "red" }}>
            <StopOutlined />
          </span>
        ),
    },
    {
      title: "Frais Réels",
      dataIndex: "montdoc",
      key: "montdoc",
      align: "right",
      onCell: () => ({ style: { paddingRight: 12, fontWeight: 'bold' } }),
      sorter: (a, b) => a.montdoc - b.montdoc,
      render: (value) => formatCellAmount(value),
    },

    {
      title: "QP IPM",
      dataIndex: "qpmut",
      key: "qpmut",
      align: "right",
      onCell: () => ({ style: { paddingRight: 12, fontWeight: 'bold' } }),
      sorter: (a, b) => a.qpmut - b.qpmut,
      render: (value) => formatCellAmount(value),
    },
    {
      title: "QP Part",
      dataIndex: "ticket",
      key: "ticket",
      align: "right",
      onCell: () => ({ style: { paddingRight: 12, fontWeight: 'bold' } }),
      sorter: (a, b) => a.ticket - b.ticket,
      render: (value) => formatCellAmount(value),
    },
    {
      title: "Frais Exclus",
      dataIndex: "fexclus",
      key: "fexclus",
      align: "right",
      onCell: () => ({ style: { paddingRight: 12, fontWeight: 'bold' } }),
      sorter: (a, b) => a.fexclus - b.fexclus,
      render: (value) => formatCellAmount(value),
    },

    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        // Vérifiez si le typedoc est actif
        const type = typeDocData.find((t) => t.typedoc === record.typedoc);
        if (!type || !type.actif) {
          return null; // Si non actif, ne rien afficher
        }
        if (record.etat === "OK") {
          return null;
        }
        return (
          <Row justify="space-around">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: "green" }} />}
              onClick={() => handleEdit(record)}
            />
            <Button
              type="text"
              icon={<PrinterOutlined style={{ color: "blue" }} />}
              onClick={() => handlePrint(record)}
            />
            <Button
              type="text"
              icon={<DeleteOutlined style={{ color: "red" }} />}
              onClick={() => handleDelete(record)}
            />
          </Row>
        );
      },
    },
  ];

  // Gestion des actions

  const handleEdit = (record) => {
    setIsEditMode(true);
    setEditingRecord(record);

    // Définir les champs principaux dans le formulaire
    form.setFieldsValue({
      ...record,
    });

    // Appeler manuellement les fonctions de gestion pour mettre à jour les dépendances
    if (record.typebenef) {
      handleTypeBenefChange(record.typebenef); // Mettre à jour les bénéficiaires
    }

    if (record.codeprest) {
      handleSpecialiteChange(record.codeprest, record.codefourn); // Mettre à jour les fournisseurs sans écraser la valeur
    }

    // Ouvrir le modal pour l'édition
    setIsModalOpen(true);
  };




  const handlePrint = async (record) => {
    // Afficher le message de chargement
    const loadingMessage = messageApi.open({
      type: 'loading',
      content: 'Impression en cour..',
      duration: 0,
    });
    // Dismiss manually and asynchronously
    setTimeout(messageApi.destroy, 2500); // `0` pour garder le message ouvert

    try {
      const response = await mesServices.printDocumentService(record.numdoc, numcont);
      // Fermer le message de chargement
      loadingMessage();

      // Afficher une notification de succès
      /* api.success({
        message: "Succès",
        description: "Le document a été imprimé avec succès.",
      }); */
    } catch (error) {
      console.error("Erreur lors de l'impression :", error);

      // Fermer le message de chargement en cas d'erreur
      loadingMessage();

      // Afficher une notification d'erreur
      api.error({
        message: "Erreur",
        description: "Une erreur s'est produite lors de l'impression. Veuillez réessayer.",
      });
    }
  };


  const handleDelete = async (record) => {
    try {
      console.log(record);

      modal.confirm({
        title: "Êtes-vous sûr de vouloir supprimer ce document ?",
        okText: "Oui",
        cancelText: "Non",
        onOk: async () => {
          try {
            await mesServices.deleteDoc(record.numdoc, numcont);
            fetchDocuments();
            api.success({
              message: "Succès",
              description: "Le document a été supprimé avec succès.",
            });
          } catch (error) {
            console.error("Erreur lors de la suppression du document :", error);
            api.error({
              message: "Erreur",
              description: "Une erreur s'est produite. Veuillez réessayer.",
            });
          }
        },
        onCancel() {
          // Annulation
        },
      });
    } catch (error) {
      console.error(
        "Erreur lors de la confirmation de la suppression :",
        error
      );
    }
  };

  const handleTypeBenefChange = (typebenef) => {
    // Filtrer les bénéficiaires correspondant au type sélectionné
    const filtered = benefData.filter((benef) => benef.codebenef === typebenef);
    setFilteredBenefData(filtered);

    // Activer ou désactiver le champ en fonction des résultats
    if (filtered.length > 0) {
      setIsTypeBenefSelected(false); // Dégriser si des données sont disponibles
      form.setFieldsValue({
        matribenef: filtered[0].matribenef, // Définir la première option par défaut
      });
    } else {
      setIsTypeBenefSelected(true); // Griser si aucune donnée
      form.setFieldsValue({ matribenef: null }); // Réinitialiser
    }
  };

  const handleSpecialiteChange = (codespe, nextCodeFourn) => {
    // Trouver une prestation par défaut pour la spécialité sélectionnée
    const defaultPrestation = prestationData.find(
      (prestation) => prestation.codeprest === codespe
    );

    // Mettre à jour la valeur du formulaire pour la prestation
    form.setFieldsValue({
      natprest: defaultPrestation ? defaultPrestation.codeprest : null, // Définir une valeur par défaut ou réinitialiser
    });

    // Filtrer les fournisseurs pour la spécialité choisie
    const filteredFournisseurs = fournisseurData.filter(
      (fournisseur) => fournisseur.codespe === codespe
    );

    // Ajouter le fournisseur spécial uniquement si des données existent
    if (fournisseurData.length > 0 && filteredFournisseurs.length > 0) {
      const specialFournisseur = fournisseurData.find(
        (fournisseur) => fournisseur.numFourn === "99999"
      );

      if (specialFournisseur) {
        filteredFournisseurs.unshift(specialFournisseur); // Ajouter au début de la liste
      }
    }

    // Mettre à jour les fournisseurs filtrés
    setFilteredFournisseurs(filteredFournisseurs);

    // Activer ou désactiver le champ en fonction des résultats
    if (filteredFournisseurs.length > 0) {
      setIsCodeSpeSelected(false); // Dégriser si des données sont disponibles
      const hasProvided = nextCodeFourn && filteredFournisseurs.some((f) => f.numFourn === nextCodeFourn);
      const codeFournToSet = hasProvided
        ? nextCodeFourn
        : (filteredFournisseurs[0]?.numFourn ?? null);
      form.setFieldsValue({
        codefourn: codeFournToSet,
      });
    } else {
      setIsCodeSpeSelected(true); // Griser si aucune donnée
      form.setFieldsValue({ codefourn: null }); // Réinitialiser
    }
  };

  // useEffect(() => {
  //   const activeDocs = typeDocData.filter((type) => type.actif === true);
  //   setFilteredTypeDocData(activeDocs);

  //   // Définir le premier document actif comme valeur par défaut
  //   if (activeDocs.length > 0) {
  //     form.setFieldsValue({
  //       typedoc: activeDocs[0].typedoc, // Définit le premier document actif par défaut
  //     });
  //   }

  //   if (prestationData.length > 0) {
  //     // Définir la première prestation comme valeur par défaut
  //     form.setFieldsValue({
  //       natprest: prestationData[0].codeprest,
  //     });
  //   }

  //   if (fournisseurData.length > 0) {
  //     // Définir le dernier fournisseur comme valeur par défaut
  //     form.setFieldsValue({
  //       codepresc: fournisseurData[fournisseurData.length - 1].numFourn,
  //     });
  //   }
  // }, [typeDocData, fournisseurData, prestationData, form]);

  const handleExport = () => {
    try {
      // Préparer les données à exporter
      const exportData = documents.map((item) => ({
        Numéro: item.numdoc,
        Type: (() => {
          switch (item.typebenef) {
            case "1":
              return "Participant";
            case "2":
              return "Epouse";
            case "3":
              return "Enfant";
            case "4":
              return "Epoux";
            case "5":
              return "Ascendant";
            case "3":
              return "Autre";
            default:
              return "Inconnu";
          }
        })(),
        Etat: item.etat === "OK" ? "Traité" : "Non Traité",
        fournisseur: item.fournisseur,
        beneficiaire: item.beneficiaire,
        Date: item.datedoc
          ? dayjs(item.datedoc).format("DD/MM/YYYY")
          : "Non défini",
      }));

      // Créer une feuille de calcul à partir des données
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Créer un classeur
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Documents");

      // Générer et télécharger le fichier Excel
      XLSX.writeFile(workbook, "Documents.xlsx");
      notification.success({ message: "Fichier exporté avec succès !" });
    } catch (error) {
      console.error("Erreur lors de l'exportation :", error);
      notification.error({
        message: "Erreur",
        description: "Une erreur s'est produite lors de l'exportation.",
      });
    }
  };

  return (
    <div
      style={{
        padding: "20px",
        background: "linear-gradient(115deg, #eef2fc 0%, #e4f5ec 38%, #f9eedd 100%)",
        minHeight: "100vh",
        transition: "background 0.7s ease-in-out"
      }}>
      {contextHolder}
      {notificationHolder}
      {messageHolder}
      <h2 style={{ marginBottom: "20px", display: "flex", alignItems: "center", gap: 10 }}>
        <CopyFilled style={{ color: "#1f4ea1", fontSize: 28 }} />
        Mes Documents
      </h2>

      <Row justify="space-between" style={{ marginBottom: "10px" }}>
        <Col>
          <Select
            value={selectedYear}
            onChange={handleYearChange}
            style={{ width: 150, marginRight: "10px" }}
          >
            {exercices.map((year) => (
              <Select.Option key={year} value={year}>
                {year}
              </Select.Option>
            ))}
          </Select>
          <Select
            value={filterEtat}
            onChange={(value) => setFilterEtat(value)} // Mise à jour du filtre
            style={{ width: 150 }}
          >
            <Select.Option value="Tous">Tous</Select.Option>
            <Select.Option value="OK">Traités</Select.Option>
            <Select.Option value="KO">Non Traités</Select.Option>
          </Select>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ marginRight: "10px" }}
            onClick={showModal}
          >
            Créer
          </Button>
          <Button
            type="default"
            className="buttonExport"
            onClick={handleExport}
            icon={<ExportOutlined />}
          >
            Export
          </Button>
        </Col>
      </Row>
      <Card
        title={
          <span style={{ textAlign: "left", display: "block" }}>
            Documents
          </span>
        }
        size="small"
      >
        <Table
          columns={columns}
          loading={loading}
          dataSource={filteredDocuments}
          onChange={handleTableChange}
          scroll={{ x: true }}
          rowKey="numdoc" // Clé unique pour chaque ligne
          bordered
          style={{ cursor: "pointer", whiteSpace: "nowrap", width: "100%" }}
          className="custom-header-table my-custom-table"
          pagination={{ defaultPageSize: 10, showSizeChanger: true }}
          summary={() => {
            return (
              <Table.Summary.Row style={{ backgroundColor: "#f0f2f5", textAlign: "right" }}>
                <Table.Summary.Cell
                  index={0}
                  colSpan={7}
                  style={{ textAlign: "right", paddingLeft: 2 }}
                >
                  <strong>Total lignes : {summaryData.length}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={7}
                  style={{ textAlign: "right", paddingRight: 2 }}
                >
                  <strong>{formatAmount(totals.montdoc)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={8}
                  style={{ textAlign: "right", paddingRight: 2 }}
                >
                  <strong>{formatAmount(totals.qpmut)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={9}
                  style={{ textAlign: "right", paddingRight: 2 }}
                >
                  <strong>{formatAmount(totals.ticket)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={10}
                  style={{ textAlign: "right", paddingRight: 2 }}
                >
                  <strong>{formatAmount(totals.fexclus)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={11} />
              </Table.Summary.Row>
            );
          }}
        />

      </Card>
      <Modal
        maskClosable={false}
        title={
          isEditMode ? "Modifier un Document" : "Créer un Nouveau Document"
        }
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Annuler
          </Button>,
          <Button key="submit" type="primary" onClick={handleOk}>
            {isEditMode ? "Mettre à Jour" : "Enregistrer"}
          </Button>,
        ]}
        width={800}
        style={{ top: 20 }}
      >
        <Form layout="vertical" form={form}>
          <Row gutter={16}>
            {/* Type de Document */}
            <Col span={12}>
              <Form.Item label="Type de Document" name="typedoc" rules={[{ required: true }]}>
                <Select placeholder="Choisir" onChange={handleTypedocChange} >
                  {typeDocData.map((type) => (
                    <Select.Option key={type.typedoc} value={type.typedoc}>
                      {type.libdoc}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            {/* Bénéficiaire */}
            <Col span={12}>
              {/*  <div style={{ border: "1px solid ##01706c", padding: "16px", borderRadius: "8px" }}> */}

              <Divider style={{ border: "#01706c" }} orientation="left">
                Bénéficiaire
              </Divider>
              <Form.Item label="Type de Bénéf." name="typebenef" rules={[{ required: true }]}>
                <Select
                  placeholder="Choisir"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={handleTypeBenefChange}
                >
                  <Option value="1">PARTICIPANT</Option>
                  <Option value="2">EPOUSE</Option>
                  <Option value="3">ENFANT</Option>
                  <Option value="4">EPOUX</Option>
                  <Option value="5">ASCENDANT</Option>
                  <Option value="6">AUTRE</Option>
                </Select>
              </Form.Item>
              <Form.Item label="Bénéficiaire" name="matribenef" rules={[{ required: true }]}>
                <Select
                  disabled={isTypeBenefSelected}
                  style={{ width: "100%" }}
                  placeholder="Choisir"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {filteredBenefData.map((benef) => (
                    <Option key={benef.matribenef} value={benef.matribenef}>
                      {isEditMode &&
                        editingRecord?.matribenef === benef.matribenef
                        ? `${editingRecord.matribenef} - ${editingRecord.beneficiaire}`
                        : `${benef.matribenef} - ${benef.prenoms}`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {/*  </Form.Item> */}
            </Col>

            {/* Prestataire */}
            <Col span={12}>
              <Divider style={{ border: "#01706c" }} orientation="left">
                {" "}
                Prestataire
              </Divider>
              <Form.Item label="Spécialité" name="codeprest" rules={[{ required: true }]}>
                <Select
                  disabled={isTypeDocSelected}
                  style={{ width: "100%" }}
                  placeholder="Choisir"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={handleSpecialiteChange} // Ajouter la gestion de l'événement
                >
                  {specialiteData &&
                    specialiteData.map((specialite) => (
                      <Option
                        key={specialite.codespe}
                        value={specialite.codespe}
                      >
                        {`${specialite.codespe} - ${specialite.intitule}`}
                      </Option>
                    ))}
                  {/* {filteredSpecialiteData.map((specialite) => (
                    <Select.Option key={specialite.codespe} value={specialite.codespe}>
                      {`${specialite.codespe} - ${specialite.intitule}`}
                    </Select.Option>
                  ))} */}
                </Select>
              </Form.Item>
              {/* 
              <Form.Item label="Fournisseur" name="codefourn">
                <Select
                  disabled={isCodeSpeSelected}
                  style={{ width: "100%" }}
                  placeholder="Choisir"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                > */}
              <Form.Item label="Fournisseur" name="codefourn" rules={[{ required: true }]}>
                <Select
                  disabled={isCodeSpeSelected}
                  style={{ width: "100%" }}
                  placeholder="Choisir"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {filteredFournisseurs.map((fournisseur) => (
                    <Option
                      key={fournisseur.numFourn}
                      value={fournisseur.numFourn}
                    >
                      {isEditMode &&
                        editingRecord?.codefourn === fournisseur.numFourn
                        ? `${editingRecord.codefourn} - ${editingRecord.fournisseur}`
                        : `${fournisseur.numFourn} - ${fournisseur.raisoc}`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {/*  </Select>
              </Form.Item> */}
              {/*  </div> */}
            </Col>
          </Row>
          <Divider style={{ color: "#01706c", border: "solid 1px" }}></Divider>
          <Row gutter={16} /* style={{ marginTop: "16px" }} */>
            {/* Prescripteur */}
            <Col span={12}>
              <Form.Item label="Prescripteur" name="codepresc">
                <Select
                  style={{ width: "100%" }}
                  placeholder="Choisir"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {/* Liste des fournisseurs */}
                  {fournisseurData.map((fournisseur, index) => (
                    <Option
                      key={fournisseur.numFourn}
                      value={fournisseur.numFourn}
                    >
                      {index === fournisseurData.length - 1
                        ? "Fournisseur de Soins Divers" // Afficher cette option pour le dernier fournisseur
                        : `${fournisseur.raisoc} - ${fournisseur.intitule}`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* Nature Prestation */}
            <Col span={12}>
              <Form.Item label="Nature Prestation" name="natprest" rules={[{ required: true }]}>
                <Select
                  disabled={isTypeDocSelected}
                  style={{ width: "100%" }}
                  placeholder="Choisir"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {prestationData.map((prestation) => (
                    <Option
                      key={prestation.codeprest}
                      value={prestation.codeprest}
                    >
                      {`${prestation.codeprest} - ${prestation.intitule}`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default Documents;
